import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ClockifyFileInventory,
  ClockifyImportPreview,
  ClockifyImportResult,
  ClockifyImportRunItem,
  ClockifyMappingDecision,
  ClockifyRowSource,
  ClockifyUndoResult,
} from '@hygge/shared';
import { api } from './api';

/**
 * The one-time Clockify → Time Tracker migration, from the screen's side.
 *
 * Everything here sits behind `time.import`. Clockify is retired once this has
 * run, so there is no polling, no cursor and no background refresh anywhere in
 * this file, and nothing here should grow one.
 */

// ── Wire shapes the client only reads ────────────────────────────────────────
//
// Declared here rather than in @hygge/shared because they are the JSON the
// endpoints happen to answer with, not a contract both sides compute against —
// the same split the Jira import lib makes. The shapes that ARE a contract
// (preview, mappings, runs, the file inventory) come from shared.

export interface ClockifyStatus {
  connected: boolean;
  workspaceId?: string;
  workspaceName?: string | null;
  userCount?: number | null;
  timeZone?: string | null;
  region?: string | null;
  status?: string;
  lastCheckedAt?: string | null;
}

/** What a live probe reports. `POST /clockify/test` answers with this. */
export interface ClockifyWorkspaceProbe {
  workspaceId: string;
  workspaceName: string;
  /** Verbatim from Clockify's `featureSubscriptionType` — "FREE" here. null when it stops sending one. */
  plan: string | null;
  userCount: number;
  activeUserCount: number;
  timeZone: string;
  region: string | null;
}

export interface ClockifyMappingOptions {
  projects: Array<{ id: string; name: string; color: string | null; status: string }>;
  employees: Array<{ id: string; firstName: string; lastName: string; workEmail: string; status: string }>;
}

/** `GET /clockify/mappings` — the stored decisions, plus the two dropdown lists. */
export interface ClockifyMappingsResponse {
  workspaceId: string;
  projects: Array<{ clockifyProjectId: string; clockifyProjectName: string; decision: ClockifyMappingDecision; projectId: string | null }>;
  users: Array<{ clockifyUserId: string; clockifyUserName: string; clockifyUserEmail: string | null; decision: ClockifyMappingDecision; employeeId: string | null }>;
  options: ClockifyMappingOptions;
}

export interface SaveMappingsPayload {
  projects?: Array<{ clockifyProjectId: string; clockifyProjectName: string; decision: ClockifyMappingDecision; projectId?: string | null }>;
  users?: Array<{
    clockifyUserId: string;
    clockifyUserName: string;
    clockifyUserEmail?: string | null;
    decision: ClockifyMappingDecision;
    employeeId?: string | null;
  }>;
}

export interface PreviewPayload {
  from: string;
  to: string;
  source: ClockifyRowSource;
  timeZone?: string;
  refresh?: boolean;
}

/**
 * The same request, plus the digest of the preview it fulfils.
 *
 * The server re-resolves the mapping when the import runs, so a decision
 * re-saved between the two requests would send the hours somewhere the numbers
 * on screen never showed. Quoting `preview.confirm` is what makes "the run is
 * the preview, carried out" true across two round trips; a stale digest comes
 * back as a refusal asking for a fresh scan, not as a silent difference.
 */
export type ImportPayload = PreviewPayload & { confirm: string };

export const clockifyKeys = {
  all: ['clockify'] as const,
  status: () => [...clockifyKeys.all, 'status'] as const,
  files: () => [...clockifyKeys.all, 'files'] as const,
  mappings: (workspaceId: string | null) => [...clockifyKeys.all, 'mappings', workspaceId ?? 'none'] as const,
  runs: () => [...clockifyKeys.all, 'runs'] as const,
};

export async function getClockifyStatus(): Promise<ClockifyStatus> {
  const { data } = await api.get<ClockifyStatus>('/clockify/status');
  return data;
}

export function useClockifyStatus(enabled = true) {
  return useQuery({ queryKey: clockifyKeys.status(), queryFn: getClockifyStatus, retry: false, enabled });
}

/**
 * The `clockify-YYYY-MM.json` exports already on the server's disk.
 *
 * Reads no Clockify API and needs no connection, which is the point: it is what
 * makes the range step able to say what a free rehearsal would cover.
 */
export function useClockifyFiles(enabled = true) {
  return useQuery({
    queryKey: clockifyKeys.files(),
    queryFn: async () => (await api.get<ClockifyFileInventory>('/clockify/files')).data,
    retry: false,
    enabled,
  });
}

export function useConnectClockify() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { workspaceId: string; apiKey: string }) =>
      (await api.post<ClockifyStatus>('/clockify/connect', payload)).data,
    // Clockify's own refusal is shown next to the form; the global toast would
    // double-display it.
    meta: { suppressGlobalToast: true },
    onSuccess: () => qc.invalidateQueries({ queryKey: clockifyKeys.all }),
  });
}

export function useTestClockify() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await api.post<ClockifyWorkspaceProbe>('/clockify/test')).data,
    meta: { suppressGlobalToast: true },
    // The probe refreshes the stored name, zone and user count server-side.
    onSuccess: () => qc.invalidateQueries({ queryKey: clockifyKeys.status() }),
  });
}

export function useDisconnectClockify() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await api.delete<ClockifyStatus>('/clockify/connection')).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: clockifyKeys.all }),
  });
}

/**
 * Stored decisions and the dropdown lists.
 *
 * The workspace id is explicit so a rehearsal off disk works with no credentials
 * stored at all — the preview reports which workspace the files belong to, and
 * that is what gets passed here.
 */
export function useClockifyMappings(workspaceId: string | null) {
  return useQuery({
    queryKey: clockifyKeys.mappings(workspaceId),
    queryFn: async () =>
      (await api.get<ClockifyMappingsResponse>('/clockify/mappings', { params: workspaceId ? { workspaceId } : undefined })).data,
    enabled: workspaceId !== null,
    retry: false,
  });
}

export function useSaveClockifyMappings(workspaceId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: SaveMappingsPayload) =>
      (
        await api.put('/clockify/mappings', payload, {
          params: workspaceId ? { workspaceId } : undefined,
        })
      ).data,
    meta: { suppressGlobalToast: true },
    onSuccess: () => qc.invalidateQueries({ queryKey: clockifyKeys.mappings(workspaceId) }),
  });
}

/**
 * Reads rows and reports. WRITES NOTHING.
 *
 * A mutation rather than a query on purpose: it is an action with a cost (on the
 * API source, requests against a Free-plan budget), and it must happen when the
 * owner asks for it — not on a mount, a refocus or a cache expiry.
 */
export function useClockifyPreview() {
  return useMutation({
    mutationFn: async (payload: PreviewPayload) => (await api.post<ClockifyImportPreview>('/clockify/preview', payload)).data,
    meta: { suppressGlobalToast: true },
  });
}

export function useRunClockifyImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: ImportPayload) => (await api.post<ClockifyImportResult>('/clockify/import', payload)).data,
    meta: { suppressGlobalToast: true },
    onSuccess: () => qc.invalidateQueries({ queryKey: clockifyKeys.runs() }),
  });
}

export function useClockifyRuns(enabled = true) {
  return useQuery({
    queryKey: clockifyKeys.runs(),
    queryFn: async () => (await api.get<ClockifyImportRunItem[]>('/clockify/runs')).data,
    retry: false,
    enabled,
  });
}

export function useUndoClockifyRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (runId: string) => (await api.post<ClockifyUndoResult>(`/clockify/runs/${runId}/undo`)).data,
    meta: { suppressGlobalToast: true },
    onSuccess: () => qc.invalidateQueries({ queryKey: clockifyKeys.runs() }),
  });
}
